import React, { Component, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";

import OverviewPage from "./components/OverviewPage.jsx";
import Dashboard from "./components/Dashboard.jsx";
import ReviewPane from "./components/ReviewPane.jsx";
import CampaignPreview from "./components/CampaignPreview.jsx";
import UploadPage from "./components/UploadPage.jsx";
import DedupLog from "./components/DedupLog.jsx";
import CampaignsPage from "./components/CampaignsPage.jsx";
import PoliciesPage from "./components/PoliciesPage.jsx";
import AuditLogPage from "./components/AuditLogPage.jsx";
import SettingsPage from "./components/SettingsPage.jsx";
import LoginPage, { isAuthenticated, logout, getCurrentUser } from "./components/LoginPage.jsx";
import "./styles.css";

// ---------------------------------------------------------------------------
// Top-level error boundary — wraps the whole app so a render throw in any
// route component degrades to a visible error screen instead of a blank page.
// ---------------------------------------------------------------------------

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Longship UI] Uncaught render error:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <pre>{String(this.state.error?.message || this.state.error)}</pre>
          <button type="button" onClick={() => this.setState({ error: null })}>
            Dismiss
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// DesktopOnly — the app is built and supported for desktop/laptop widths only.
// Below 900px we show a notice instead of a broken layout. Rendered on every
// route (and on the login screen), so it applies to the whole app.
//
// The threshold intentionally matches the narrowest breakpoint the stylesheet
// handles (900px) — below that the nav bar wraps and the review columns stack.
// ---------------------------------------------------------------------------

const DESKTOP_MIN_WIDTH = 900;

/** True while the viewport is narrower than the supported desktop minimum. */
function useIsNarrowViewport() {
  // null until the first effect runs, so we never flash the notice on wide
  // screens before the media query is evaluated.
  const [narrow, setNarrow] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${DESKTOP_MIN_WIDTH - 1}px)`);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return narrow === true;
}

function DesktopOnly() {
  const narrow = useIsNarrowViewport();
  if (!narrow) return null;

  return (
    <div className="desktop-only" role="alert">
      <div className="desktop-only-card">
        <img src="/logo.svg" alt="" className="desktop-only-mark" />
        <h1>Please use this application on a desktop or laptop</h1>
        <p>
          Longship needs a wider screen than this device provides. Open it on a
          desktop or laptop computer to review and approve insertion orders.
        </p>
        <p className="desktop-only-hint">
          This screen is {window.innerWidth}px wide; at least {DESKTOP_MIN_WIDTH}px
          is required.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — app title + current user + nav. Shown on every route.
// ---------------------------------------------------------------------------

function Header() {
  const location = useLocation();
  // Computed per render (not at module load): with SSO the identity only
  // exists after the check-sso init resolves, which happens after mount.
  const currentUser = getCurrentUser();
  return (
    <header className="app-header">
      <div className="app-header-left">
        <span className="app-logo">
          <img src="/logo.svg" alt="Longship" className="app-logo-mark" />
          Longship
        </span>
        <nav className="app-nav">
          <NavLink to="/" end className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Overview
          </NavLink>
          <NavLink to="/upload" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            New IO
          </NavLink>
          <NavLink to="/dashboard" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Dashboard
          </NavLink>
          <NavLink to="/campaigns" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Campaigns
          </NavLink>
          <NavLink to="/policies" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Policies
          </NavLink>
          <NavLink to="/dedup" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Dedup Log
          </NavLink>
          <NavLink to="/audit-log" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Audit Log
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Settings
          </NavLink>
        </nav>
      </div>
      <div className="app-header-right">
        <span className="app-user" title="Signed in user">{currentUser}</span>
        <button type="button" className="btn btn-sm btn-danger-outline" onClick={logout} title="Sign out">
          Sign Out
        </button>
      </div>
      <div className="app-header-route" title="Current route">{location.pathname}</div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// App — BrowserRouter + route table. Suspense + error boundary wrap routes.
// ---------------------------------------------------------------------------

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const narrowViewport = useIsNarrowViewport();

  // Desktop/laptop only. Checked before auth so the notice covers the login
  // screen too, and before the app tree so nothing else renders underneath it.
  if (narrowViewport) return <DesktopOnly />;

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className="app-root">
          <Header />
          <main className="app-main">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<OverviewPage />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/ios/:id" element={<ReviewPane />} />
                <Route path="/ios/:id/preview" element={<CampaignPreview />} />
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/policies" element={<PoliciesPage />} />
                <Route path="/dedup" element={<DedupLog />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {/* Fallback for unknown routes */}
                <Route
                  path="*"
                  element={
                    <div className="not-found">
                      <h2>404</h2>
                      <p>No route matched this path.</p>
                    </div>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}